// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BankAccountType, type BankAccount } from '@client/banks/types/account';
import { toFormOptionalIban } from '@client/banks/utils/account-reference';
import type { AdvisoryBankAccount } from '@shared/data-access/types/brokerage-types';
import dayjs from 'dayjs';

const resolveAccountType = (type?: string | null): BankAccountType =>
  Object.values(BankAccountType).includes(type as BankAccountType)
    ? (type as BankAccountType)
    : BankAccountType.Checking;

export const mapAdvisoryBankAccountToBankAccount = (
  account: AdvisoryBankAccount,
): BankAccount => {
  const owners = (account.owners ?? [])
    .map(owner => ({ name: (owner.name ?? '').trim() }))
    .filter(owner => owner.name.length > 0);

  const balanceDate = account.valid_from
    ? dayjs(account.valid_from).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  const name = account.name?.trim() ?? '';

  return {
    iban: toFormOptionalIban(account.iban),
    name,
    currency: account.currency ?? 'EUR',
    accountType: resolveAccountType(account.type),
    available: account.available ?? account.cash ?? 0,
    balanceDate,
    owners: owners.length > 0 ? owners : [{ name: name || '—' }],
  };
};
